<template>
    <!-- Paginación de las listas de la cuenta: Anterior / números (con … si son
         muchas) / Siguiente, y "Página X de Y · N resultados". -->
    <nav v-if="ultima > 1" class="bk-pag" :aria-label="$t('nuevo_menu.pag.label')">
        <button type="button" class="bk-pag-btn bk-pag-btn--nav" :disabled="actual <= 1" @click="ir(actual - 1)">
            <span aria-hidden="true" v-html="ICON.back"></span><span class="bk-pag-txt">{{ $t('nuevo_menu.pag.prev') }}</span>
        </button>

        <ol class="bk-pag-list">
            <li v-for="(p, i) in paginas" :key="`${p}-${i}`">
                <span v-if="p === '…'" class="bk-pag-gap" aria-hidden="true">…</span>
                <button
                    v-else
                    type="button"
                    :class="['bk-pag-btn', 'bk-pag-num', { 'is-on': p === actual }]"
                    :aria-current="p === actual ? 'page' : null"
                    :aria-label="$t('nuevo_menu.pag.page', { n: p })"
                    @click="ir(p)"
                >{{ p }}</button>
            </li>
        </ol>
        <!-- En celular, en vez de todos los números: "3 / 12" -->
        <span class="bk-pag-mini" aria-hidden="true">{{ actual }} / {{ ultima }}</span>

        <button type="button" class="bk-pag-btn bk-pag-btn--nav" :disabled="actual >= ultima" @click="ir(actual + 1)">
            <span class="bk-pag-txt">{{ $t('nuevo_menu.pag.next') }}</span><span aria-hidden="true" v-html="ICON.next"></span>
        </button>

        <p class="bk-pag-info">
            {{ $t('nuevo_menu.pag.info', { n: actual, total: ultima }) }}<template v-if="total != null"> · {{ $tc('nuevo_menu.pag.items', total, { n: total }) }}</template>
        </p>
    </nav>
</template>

<script>
import ICON from './iconos';

export default {
    name: 'BkPaginacion',
    props: {
        actual: { type: Number, required: true },
        ultima: { type: Number, required: true },
        total: { type: Number, default: null },
    },
    data() {
        return { ICON };
    },
    computed: {
        // 1 … 4 5 [6] 7 8 … 20 (siempre primera y última; dos a cada lado).
        paginas() {
            const { actual, ultima } = this;
            if (ultima <= 7) return Array.from({ length: ultima }, (_, i) => i + 1);
            const desde = Math.max(2, actual - 2);
            const hasta = Math.min(ultima - 1, actual + 2);
            const out = [1];
            if (desde > 2) out.push('…');
            for (let p = desde; p <= hasta; p++) out.push(p);
            if (hasta < ultima - 1) out.push('…');
            out.push(ultima);
            return out;
        },
    },
    methods: {
        ir(p) {
            const n = Math.min(this.ultima, Math.max(1, p));
            if (n !== this.actual) this.$emit('ir', n);
        },
    },
};
</script>

<style>
.bk-pag {
    display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 10px 12px;
    margin: 10px 0 0; padding: 14px; border-radius: var(--bk-radius-lg, 22px);
    background: rgba(255, 255, 255, .84); border: 1px solid rgba(29, 26, 23, .07);
    box-shadow: 0 30px 80px -40px rgba(122, 57, 0, .28), 0 8px 24px -16px rgba(29, 26, 23, .12);
}
.bk-pag ol, .bk-pag li, .bk-pag p { margin: 0; padding: 0; list-style: none; }
.bk-pag-list { display: flex; align-items: center; gap: 6px; }
.bk-pag-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    min-width: 44px; height: 44px; padding: 0 12px; margin: 0;
    border: 1.5px solid var(--bk-line-2, #ddd2c7); border-radius: 999px; background: #fff;
    font: inherit; font-size: 15px; font-weight: 700; color: var(--bk-ink, #1d1a17);
    font-variant-numeric: tabular-nums; cursor: pointer;
    transition: border-color .15s, background .15s, color .15s, transform .15s, box-shadow .2s;
}
.bk-pag-btn svg { width: 18px; height: 18px; }
.bk-pag-btn > span:not(.bk-pag-txt) { display: contents; }
.bk-pag-btn:hover:not(:disabled) { border-color: var(--bk-orange, #ff7a00); color: var(--bk-orange-dark, #e8640a); transform: translateY(-1px); }
.bk-pag-btn:focus { outline: none; }
.bk-pag-btn:focus-visible { outline: 2px solid var(--bk-orange, #ff7a00); outline-offset: 2px; }
.bk-pag-btn:disabled { opacity: .4; cursor: not-allowed; }
.bk-pag-btn--nav { padding: 0 18px; }
.bk-pag-num.is-on {
    border-color: transparent; color: #fff; cursor: default;
    background: linear-gradient(180deg, #ff8414, #f26b05);
    box-shadow: 0 8px 18px -8px rgba(232, 100, 10, .8);
}
.bk-pag-num.is-on:hover { transform: none; color: #fff; }
.bk-pag-gap { display: inline-grid; place-items: center; min-width: 22px; color: var(--bk-muted, #6f6760); font-weight: 700; }
.bk-pag-mini { display: none; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--bk-ink, #1d1a17); }
.bk-pag-info { flex-basis: 100%; text-align: center; font-size: 13px; color: var(--bk-muted, #6f6760); }
/* Página nueva cargando: la lista se atenúa y no se puede tocar. */
.bk-list-main.is-cargando > .bk-rv, .bk-list-main.is-cargando > .bk-pag { opacity: .45; pointer-events: none; transition: opacity .2s; }
@media (max-width: 640px) {
    .bk-pag { gap: 10px; padding: 12px; }
    .bk-pag-list { display: none; }
    .bk-pag-mini { display: inline; min-width: 64px; text-align: center; }
    .bk-pag-btn--nav { flex: 1 1 0; }
}
@media (max-width: 380px) {
    .bk-pag-btn--nav .bk-pag-txt { display: none; }
    .bk-pag-btn--nav { flex: 0 0 auto; min-width: 52px; }
}
</style>
